import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { createCustomIcon } from "../../components/Common/Map/helper";
import { formatDate } from "../../helpers/formatDate";
import { useEffect } from "react";
import L from "leaflet";


// Component to handle auto-fitting the map bounds
function AutoFitBounds({ locations }) {
  const map = useMap();

  useEffect(() => {
    if (locations && locations.length > 0) {
      // Create bounds from all locations
      const bounds = locations.reduce((bounds, location) => {
        return bounds.extend(location);
      }, new L.LatLngBounds());

      // Fit the map to show all markers with padding
      map.fitBounds(bounds, {
        padding: [20, 20], // Add padding around the bounds
        maxZoom: 15, // Prevent zooming in too much for single markers
      });
    }
  }, [map, locations]);

  return null;
}

export default function CheckInMap({ filteredUsers = [], vehicles = [] }) {
  // Validate and filter user locations
  const validUsers = filteredUsers.filter((user) => {
    return (
      user &&
      user.currentLocation &&
      user.currentLocation.latitude &&
      user.currentLocation.longitude &&
      !isNaN(parseFloat(user.currentLocation.latitude)) &&
      !isNaN(parseFloat(user.currentLocation.longitude))
    );
  });

  // Validate and filter vehicle locations
  const validVehicles = vehicles.filter((vehicle) => {
    return (
      vehicle &&
      vehicle.currentLocation &&
      vehicle.currentLocation.latitude &&
      vehicle.currentLocation.longitude &&
      !isNaN(parseFloat(vehicle.currentLocation.latitude)) &&
      !isNaN(parseFloat(vehicle.currentLocation.longitude))
    );
  });

  // Combine all locations for map bounds
  const userLocations = validUsers.map((user) => [
    parseFloat(user.currentLocation.latitude),
    parseFloat(user.currentLocation.longitude),
  ]);

  const vehicleLocations = validVehicles.map((vehicle) => [
    parseFloat(vehicle.currentLocation.latitude),
    parseFloat(vehicle.currentLocation.longitude),
  ]);

  const locations = [...userLocations, ...vehicleLocations];

  // Default center (India center coordinates)
  const defaultCenter = [20.5937, 78.9629];
  const mapCenter = locations.length > 0 ? locations[0] : defaultCenter;

  return (
    <div style={{ width: "100%", height: "600px" }}>
      {/* Loading indicator */}
      {validUsers.length === 0 && validVehicles.length === 0 && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            background: "rgba(255, 255, 255, 0.9)",
            padding: "10px 20px",
            borderRadius: "5px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
          {filteredUsers.length === 0 && vehicles.length === 0
            ? "No data to display"
            : "No location data available"}
        </div>
      )}

      <MapContainer
        key="guard-location-map" // Stable key prevents remounts
        center={mapCenter}
        zoom={locations.length > 0 ? 10 : 8}
        style={{
          height: "100%",
          width: "100%",
          borderRadius: "8px",
          overflow: "hidden",
        }}
        scrollWheelZoom={true}
        attributionControl={true}
      >
        <TileLayer
          url="http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
          subdomains={["mt0", "mt1", "mt2", "mt3"]}
        />
        
        {/* Auto-fit bounds component */}
        <AutoFitBounds locations={locations} />

        {/* Render markers for valid users */}
        {validUsers.map((user) => (
          <Marker
            key={`user-${user.userId || user.id}`}
            position={[
              parseFloat(user?.currentLocation?.latitude),
              parseFloat(user?.currentLocation?.longitude),
            ]}
            icon={createCustomIcon(
              user.profilePic,
              user?.currentLocation?.updadatedAt,
              true
            )}
          >
            <Popup>
              <div style={{ minWidth: "200px" }}>
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "16px",
                    marginBottom: "8px",
                    color: "#2563eb",
                    borderBottom: "1px solid #e5e7eb",
                    paddingBottom: "4px",
                  }}
                >
                  👤 {user.firstName || "N/A"} {user.lastName || ""}
                </div>
                <div style={{ marginBottom: "4px" }}>
                  <strong>Email:</strong> {user.email || "N/A"}
                </div>
                <div style={{ marginBottom: "4px" }}>
                  <strong>Phone:</strong> {user.contactNumber || "N/A"}
                </div>
                <div>
                  <strong>Location:</strong>
                  {" "} {parseFloat(user.currentLocation.latitude).toFixed(6)}
                  {", "}
                  {parseFloat(user.currentLocation.longitude).toFixed(6)}
                </div>
                <div style={{ marginBottom: "4px" }}>
                  <strong>Last Updated:</strong> {formatDate(user?.currentLocation?.updatedAt) || "N/A"}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Render markers for valid vehicles */}
        {validVehicles.map((vehicle) => (
          <Marker
            key={`vehicle-${vehicle.userId}`}
            position={[
              parseFloat(vehicle.currentLocation.latitude),
              parseFloat(vehicle.currentLocation.longitude),
            ]}
            icon={createCustomIcon(
              vehicle?.profilePic,
              vehicle?.currentLocation?.updatedAt,
              false
            )}
          >
            <Popup>
              <div style={{ minWidth: "200px" }}>
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "16px",
                    marginBottom: "8px",
                    color: "#dc2626",
                    borderBottom: "1px solid #e5e7eb",
                    paddingBottom: "4px",
                  }}
                >
                  🚗 {vehicle.driverName || vehicle.vehicleName || "Vehicle"}
                </div>
                <div style={{ marginBottom: "4px" }}>
                  <strong>Vehicle No:</strong>{" "}
                  {vehicle.vehicleNumber || vehicle.id || "N/A"}
                </div>
                <div style={{ marginBottom: "4px" }}>
                  <strong>Driver:</strong> {vehicle.firstName + " " + vehicle.lastName}
                </div>
                <div style={{ marginBottom: "4px" }}>
                  <strong>Phone:</strong> {vehicle.contactNumber || "N/A"}
                </div>
                <div style={{ fontSize: "12px" }}>
                  <strong>Location:</strong>
                  {" "} {parseFloat(vehicle?.currentLocation?.latitude).toFixed(6)}
                  {", "}
                  {parseFloat(vehicle?.currentLocation?.longitude).toFixed(6)}
                </div>
                <div style={{ marginBottom: "4px" }}>
                  <strong>Last Updated:</strong> {formatDate(vehicle?.currentLocation?.updatedAt) || "N/A"}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}